/**
 * 3D rendering of H3 cells on a Map3DElement.
 * Mirrors h3-overlay-2d.ts using Polygon3DElement clamped to ground.
 * Maintains a polygon-per-cell cache; diffs incoming cell sets to add/update/remove.
 */

import { cellToBoundary } from 'h3-js';

type Polygon3D = google.maps.maps3d.Polygon3DElement;

interface OverlayState3D {
  map: google.maps.maps3d.Map3DElement | null;
  cells: Map<string, Polygon3D>;
  selectedCell: Polygon3D | null;
}

const state: OverlayState3D = {
  map: null,
  cells: new Map(),
  selectedCell: null,
};

interface CellRenderOptions {
  /** Cell id → fill opacity 0..1. Absence = grid-only (no fill). */
  fills?: Map<string, number>;
}

/** Render the given H3 cells onto a Map3DElement, diffing against the previous render */
export function renderH3Overlay3D(
  map3d: google.maps.maps3d.Map3DElement,
  cells: string[],
  options: CellRenderOptions = {},
): void {
  // Map instance changed (e.g. after 2D→3D switch) — drop old cache
  if (state.map && state.map !== map3d) {
    clearH3Overlay3D();
  }
  state.map = map3d;

  const next = new Set(cells);
  for (const [id, poly] of state.cells) {
    if (!next.has(id)) {
      removeChildSafe(map3d, poly);
      state.cells.delete(id);
    }
  }

  for (const cell of cells) {
    const opacity = options.fills?.get(cell) ?? 0;
    const existing = state.cells.get(cell);
    if (existing) {
      existing.fillColor = fillColorFor(opacity);
    } else {
      const poly = createCellPolygon(cell, opacity);
      state.cells.set(cell, poly);
      map3d.appendChild(poly);
    }
  }
}

/** Highlight a single cell on the 3D map (for area-query click) */
export function highlightCell3D(
  map3d: google.maps.maps3d.Map3DElement,
  cell: string | null,
): void {
  if (state.selectedCell && state.map) {
    removeChildSafe(state.map, state.selectedCell);
    state.selectedCell = null;
  }
  state.map = map3d;
  if (!cell) return;
  const poly = new google.maps.maps3d.Polygon3DElement({
    outerCoordinates: cellCoords(cell),
    altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
    strokeColor: '#ffd400',
    strokeWidth: 3,
    fillColor: '#ffd40033',
    drawsOccludedSegments: true,
  });
  state.selectedCell = poly;
  map3d.appendChild(poly);
}

/** Tear down every rendered cell */
export function clearH3Overlay3D(): void {
  if (state.map) {
    for (const poly of state.cells.values()) removeChildSafe(state.map, poly);
    if (state.selectedCell) removeChildSafe(state.map, state.selectedCell);
  }
  state.cells.clear();
  state.selectedCell = null;
  state.map = null;
}

function createCellPolygon(cell: string, opacity: number): Polygon3D {
  return new google.maps.maps3d.Polygon3DElement({
    outerCoordinates: cellCoords(cell),
    altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
    strokeColor: '#5fa3ff',
    strokeWidth: 1,
    fillColor: fillColorFor(opacity),
    drawsOccludedSegments: false,
  });
}

function cellCoords(cell: string): google.maps.LatLngAltitudeLiteral[] {
  return cellToBoundary(cell).map(([lat, lng]) => ({ lat, lng, altitude: 0 }));
}

function fillColorFor(opacity: number): string {
  if (opacity <= 0) return '#00000000';
  const a = Math.round(Math.min(1, opacity) * 255).toString(16).padStart(2, '0');
  return '#ff5b3a' + a;
}

function removeChildSafe(map3d: google.maps.maps3d.Map3DElement, child: Polygon3D): void {
  try {
    map3d.removeChild(child);
  } catch {
    // already removed
  }
}
